"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  IconButton,
  Flex,
  Textarea,
  Box,
} from "@chakra-ui/react";
import { Plus, Trash2 } from "lucide-react";
import type { PostCallAnalysisData } from "./PostCallAnalysis";

interface TextAnalysisFormProps {
  onSave: (data: PostCallAnalysisData) => void;
  onCancel: () => void;
  initialData?: PostCallAnalysisData;
}

export const TextAnalysisForm: React.FC<TextAnalysisFormProps> = ({
  onSave,
  onCancel,
  initialData,
}) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(
    initialData?.description || ""
  );
  const [examples, setExamples] = useState<string[]>(
    initialData?.examples || []
  );

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description);
      setExamples(initialData.examples || []);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      type: "string",
      name,
      description,
      examples: examples.length > 0 ? examples : undefined,
    });
  };

  const handleAddExample = () => setExamples((prev) => [...prev, ""]);

  const handleExampleChange = (index: number, value: string) => {
    const next = [...examples];
    next[index] = value;
    setExamples(next);
  };

  const handleRemoveExample = (index: number) => {
    setExamples((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box mb={4}>
      <form onSubmit={handleSubmit}>
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel fontSize="sm" color="gray.700">
              Name
            </FormLabel>
            <Input
              id="text-analysis-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="detailed_call_summary"
              size="sm"
              variant="outline"
              bg="white"
              color="gray.800"
              _placeholder={{ color: "gray.500" }}
              borderColor="gray.300"
              _hover={{ borderColor: "gray.400" }}
              _focus={{
                borderColor: "primary.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
              }}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm" color="gray.700">
              Description
            </FormLabel>
            <Textarea
              id="text-analysis-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detailed summary of the call before you transfer the call to a human agent so that the human agent can understand the context of the call"
              rows={3}
              size="sm"
              variant="outline"
              bg="white"
              color="gray.800"
              _placeholder={{ color: "gray.500" }}
              borderColor="gray.300"
              _hover={{ borderColor: "gray.400" }}
              _focus={{
                borderColor: "primary.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
              }}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm" color="gray.700">
              Format Example (Optional)
            </FormLabel>
            <VStack spacing={2} align="stretch">
              {examples.map((example, index) => (
                <Flex key={index} gap={2}>
                  <Input
                    id={`text-analysis-example-${index}`}
                    value={example}
                    onChange={(e) => handleExampleChange(index, e.target.value)}
                    placeholder="Detailed call summary of your use case"
                    size="sm"
                    variant="outline"
                    bg="white"
                    color="gray.800"
                    _placeholder={{ color: "gray.500" }}
                    borderColor="gray.300"
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{
                      borderColor: "primary.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                    }}
                  />
                  <IconButton
                    aria-label="Remove example"
                    icon={<Trash2 size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveExample(index)}
                    color="gray.600"
                    _hover={{ bg: "gray.100" }}
                  />
                </Flex>
              ))}
              <Button
                leftIcon={<Plus size={16} />}
                variant="ghost"
                size="sm"
                onClick={handleAddExample}
                color="gray.600"
                _hover={{ bg: "gray.100" }}
                alignSelf="flex-start"
              >
                Add
              </Button>
            </VStack>
          </FormControl>

          <Flex justify="flex-end" gap={2} pt={4}>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              bg="primary.500"
              color="white"
              _hover={{ bg: "primary.600" }}
            >
              Save
            </Button>
          </Flex>
        </VStack>
      </form>
    </Box>
  );
};
